import { useReveal } from "../../lib/hooks";

const FEATURES = [
  {
    glyph: "अभ्यास",
    glyphLabel: "abhyāsa — practice",
    title: "Discipline of practice",
    body: "Vedic recitation is exacting. Learning a single mantra correctly demands attention to vowel length, consonant articulation, and prescribed pitch. The discipline applies — and rewards — far beyond the cushion.",
  },
  {
    glyph: "अर्पण",
    glyphLabel: "arpaṇa — offering",
    title: "An offering, not a performance",
    body: "Chanting the Vedas isn't a performance for an audience. It's an offering — usually to whatever the chanter holds sacred. We don't prescribe the recipient; the practice does its work either way.",
  },
  {
    glyph: "नाद",
    glyphLabel: "nāda — sacred sound",
    title: "Sound, vibration",
    body: "The mantra works through its sound. A mother's lullaby quiets a child who doesn't yet know any words; the syllables of the Veda move in the same way. Meaning will come if it comes — the vibration is already enough.",
  },
  {
    glyph: "द्वार",
    glyphLabel: "dvāra — door",
    title: "Open to all — actually",
    body: "No prerequisite. You don't need to read Sanskrit, belong to a tradition, or have any background. Online sessions, periodic gatherings at the House of Unity, events across Europe. Curiosity is enough.",
  },
] as const;

export function Practice() {
  const { ref } = useReveal<HTMLDivElement>();

  return (
    <section id="practice" className="py-20 md:py-28">
      <div className="container-edit">
        <div className="section-heading">
          <span className="section-heading-num tabular">05 / Practice</span>
          <hr className="rule flex-1 hidden sm:block" />
        </div>

        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-16 mb-14 md:mb-16">
          <h2 className="col-span-12 lg:col-span-7 font-display text-[clamp(2.5rem,6vw,5rem)] leading-[1.02] tracking-[-0.02em]">
            What learning here{" "}
            <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
              actually looks like
            </span>
            .
          </h2>
          <p className="col-span-12 lg:col-span-5 lg:col-start-8 text-[1.05rem] leading-[1.7] text-[var(--color-ink-soft)] lg:max-w-[34ch] self-end">
            Learning the Vedas is not the same as learning a language or a song.
            It asks for patience, for attention, and for company. We try to provide
            the third and support the first two.
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14 md:gap-y-20 reveal-stagger">
          {FEATURES.map((f) => (
            <article
              key={f.title}
              className="flex flex-col gap-y-5 sm:grid sm:grid-cols-[8.5rem_1fr] sm:gap-x-6 sm:gap-y-0"
            >
              {/* Sanskrit glyph + label — stacks above title on mobile, side-by-side on sm+ */}
              <div className="flex sm:flex-col items-baseline sm:items-end gap-x-4 sm:gap-x-0 sm:text-right">
                <span
                  className="font-deva text-[1.85rem] sm:text-[1.85rem] leading-none block"
                  style={{ color: "var(--color-violet)" }}
                  aria-label={f.glyphLabel}
                >
                  {f.glyph}
                </span>
                <span className="font-grotesk text-[0.65rem] tracking-[0.2em] uppercase text-[var(--color-ink-mute)] sm:mt-3 block">
                  {f.glyphLabel.split(" — ")[0]}
                </span>
              </div>

              {/* Title + body */}
              <div>
                <h3 className="font-display text-[1.65rem] sm:text-[1.85rem] md:text-[2.1rem] leading-tight tracking-tight mb-4">
                  {f.title}
                </h3>
                <hr className="rule mb-5 max-w-[3rem]" />
                <p className="text-[1.0625rem] leading-[1.7] text-[var(--color-ink-soft)] max-w-[44ch]">
                  {f.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
